import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getOrders } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Loader2, ClipboardList, ArrowRight, Clock, CheckCircle } from "lucide-react";

export default function PendingOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      // Fetch both pending_review and finalized orders
      const [pendingRes, finalizedRes] = await Promise.all([
        getOrders("pending_review"),
        getOrders("finalized"),
      ]);
      
      // Combine and sort by created_at descending
      const allOrders = [...pendingRes.data, ...finalizedRes.data].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setOrders(allOrders);
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUniqueVendorCount = (items) => {
    const vendors = new Set(items.map((item) => item.vendor_name));
    return vendors.size;
  };

  return (
    <AppLayout title="Submitted Orders" showBack>
      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No submitted orders</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order, index) => {
              const isPending = order.status === "pending_review";
              const isFinalized = order.status === "finalized";
              
              return (
                <Link
                  key={order.id}
                  to={`/orders/${order.id}`}
                  className={`block animate-in stagger-${(index % 4) + 1}`}
                  data-testid={`submitted-order-${order.id}`}
                >
                  <Card className="card-hover">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge 
                              variant="secondary" 
                              className={isPending 
                                ? "bg-amber-100 text-amber-700 hover:bg-amber-100" 
                                : "bg-blue-100 text-blue-700 hover:bg-blue-100"
                              }
                            >
                              {isPending ? (
                                <>
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending Review
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Finalized
                                </>
                              )}
                            </Badge>
                            <span className="text-xs text-stone-400 font-mono">
                              #{order.order_name || order.id.substring(0, 8)}
                            </span>
                          </div>
                          <p className="text-sm text-stone-600 mt-2">
                            <span className="font-medium">{order.items.length}</span> items from{" "}
                            <span className="font-medium">{getUniqueVendorCount(order.items)}</span> vendors
                          </p>
                          <p className="text-xs text-stone-400 mt-1">
                            By {order.created_by_name} • {formatDate(order.created_at)}
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
